import { useState } from 'react';
import { SnackbarStatus, useSnackbar } from '@weareopenr/component-library';
import { canDeleteMenu, deleteMenu, isMenuNotDeletableError } from 'src/api/deleteMenu/deleteMenu';

interface UseDeleteMenuPromptArgs {
  menuId: string;
  genericErrorMessage: string;
  onDeleted: () => void | Promise<void>;
  onCheckError?: (error: unknown) => void;
  onDeleteError?: (error: unknown) => void;
}

export const useDeleteMenuPrompt = ({
  menuId,
  genericErrorMessage,
  onDeleted,
  onCheckError,
  onDeleteError,
}: UseDeleteMenuPromptArgs) => {
  const [isRemovePromptVisible, setIsRemovePromptVisible] = useState(false);
  const [isMenuInUsePromptVisible, setIsMenuInUsePromptVisible] = useState(false);
  const [menuInUseErrorMessage, setMenuInUseErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingCanDelete, setIsCheckingCanDelete] = useState(false);
  const { showSnackbar } = useSnackbar();

  const showMenuInUsePrompt = (message: string) => {
    setMenuInUseErrorMessage(message);
    setIsMenuInUsePromptVisible(true);
  };

  const closeEllipsisMenu = (originatingMenu: Element | null | undefined) => {
    if (!originatingMenu?.isConnected) return;
    originatingMenu.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
  };

  const openDeletePrompt = async (event?: Event) => {
    if (isCheckingCanDelete) return;
    const originatingMenu = event?.target instanceof Element ? event.target.closest('[role="menu"]') : null;
    try {
      setIsCheckingCanDelete(true);
      await canDeleteMenu(menuId);
      closeEllipsisMenu(originatingMenu);
      setIsRemovePromptVisible(true);
    } catch (err) {
      closeEllipsisMenu(originatingMenu);
      if (isMenuNotDeletableError(err)) {
        showMenuInUsePrompt(err.message);
        return;
      }
      onCheckError?.(err);
      showSnackbar({ status: SnackbarStatus.ERROR, message: genericErrorMessage });
    } finally {
      setIsCheckingCanDelete(false);
    }
  };

  const confirmDelete = async () => {
    try {
      setIsSubmitting(true);
      await deleteMenu(menuId);
      await onDeleted();
    } catch (err) {
      setIsRemovePromptVisible(false);
      if (isMenuNotDeletableError(err)) {
        showMenuInUsePrompt(err.message);
        return;
      }
      onDeleteError?.(err);
      showSnackbar({ status: SnackbarStatus.ERROR, message: genericErrorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isRemovePromptVisible,
    isMenuInUsePromptVisible,
    menuInUseErrorMessage,
    isSubmitting,
    isCheckingCanDelete,
    openDeletePrompt,
    closeDeletePrompt: () => setIsRemovePromptVisible(false),
    closeMenuInUsePrompt: () => setIsMenuInUsePromptVisible(false),
    confirmDelete,
  };
};
